/**
 * Landing Page - User creation and authentication entry point
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { TermsOfUse } from '../components/TermsOfUse';
import { createNewUser, getIpAddress } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Loader2, Sun, Moon } from 'lucide-react';

interface GoogleTokenPayload {
  email: string;
  name?: string;
  hd?: string;
}

interface UserFormData {
  fullName: string;
  email: string;
  orgName: string;
}

export function LandingPage() {
  const { isAuthenticated, isAdmin, login, loginWithGoogle } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [touAccepted, setTouAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [googleLoginError, setGoogleLoginError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    fullName: '',
    email: '',
    orgName: '',
  });

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Redirect based on user role
      const destination = isAdmin ? '/projects' : '/upload';
      void navigate(destination);
    }
  }, [isAuthenticated, isAdmin, navigate]);

  const handleInputChange = (field: keyof UserFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!touAccepted || !formData.email || !formData.fullName || !formData.orgName) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get IP address and submit user data
      const ip = (await getIpAddress()) ?? 'unknown';
      const date = new Date().toLocaleString();

      // Create user (fire and forget)
      void createNewUser({
        email: formData.email,
        fullName: formData.fullName,
        orgName: formData.orgName,
        date,
        ip,
      });

      // Login as regular user and navigate
      login(formData.email, 'user');
      setShowModal(false);
      setTimeout(() => navigate('/upload'), 500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    setShowModal(false);
    setTouAccepted(false);
    setFormData({ fullName: '', email: '', orgName: '' });
  };

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setGoogleLoginError('No credential received from Google');
      return;
    }

    try {
      // Decode the JWT to get user info
      const decoded = jwtDecode<GoogleTokenPayload>(credentialResponse.credential);

      // Check if user is from Picture Health domain
      if (decoded.hd !== 'picturehealth.com') {
        console.error('Domain check failed:', { hd: decoded.hd, email: decoded.email });
        setGoogleLoginError(
          `Only Picture Health employees can sign in. Detected domain: ${decoded.hd ?? 'none'}`
        );
        return;
      }

      // Login with Google credential
      loginWithGoogle(credentialResponse.credential, decoded.email);
      setShowAdminModal(false);
      setGoogleLoginError(null);

      // Check for stored redirect path (set when auth expired)
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAfterLogin');
      const destination = redirectPath ?? '/projects';
      setTimeout(() => navigate(destination), 500);
    } catch (error) {
      console.error('Google login failed:', error);
      setGoogleLoginError('Failed to process Google login');
    }
  };

  const handleGoogleError = () => {
    setGoogleLoginError('Google sign-in failed. Please try again.');
  };

  const handleAdminModalClose = () => {
    setShowAdminModal(false);
    setGoogleLoginError(null);
  };

  // Don't render anything if authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Theme toggle button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="absolute top-4 right-4"
        id="toggle-theme"
      >
        {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        <span className="sr-only">Toggle theme</span>
      </Button>

      {/* Logo */}
      <div className="absolute top-8 left-8 flex items-center gap-3">
        <img src="/images/logo.png" alt="Picture Health Logo" className="h-10 w-10" />
        <span className="font-semibold text-xl">Picture Health</span>
      </div>

      <div className="w-full max-w-md">
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="text-2xl">QVT Demo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Thank you for your interest in QVT. Please create a user to continue.
            </p>
            <div className="space-y-2">
              <Button
                className="w-full"
                data-testid="button-create-user"
                onClick={() => setShowModal(true)}
              >
                Create a User to Begin
              </Button>
              <Button
                variant="outline"
                className="w-full"
                data-testid="button-admin-signin"
                onClick={() => setShowAdminModal(true)}
              >
                Employee Sign In
              </Button>
            </div>
          </CardContent>
        </Card>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-[500px]" data-testid="user-modal">
            <DialogHeader>
              <DialogTitle>New User</DialogTitle>
            </DialogHeader>
            <form id="form-user" name="form-user" autoComplete="off" onSubmit={handleAccept}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    type="text"
                    id="fullName"
                    name="fullName"
                    autoComplete="off"
                    required
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    placeholder=""
                    autoComplete="off"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization</Label>
                  <Input
                    type="text"
                    id="orgName"
                    name="orgName"
                    autoComplete="off"
                    required
                    value={formData.orgName}
                    onChange={(e) => handleInputChange('orgName', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Terms of Use</h3>
                  <ScrollArea className="h-48 rounded-md border p-4" id="scroll-tou">
                    <TermsOfUse />
                  </ScrollArea>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="check-terms"
                    className="h-4 w-4 rounded border-input"
                    checked={touAccepted}
                    onChange={(e) => setTouAccepted(e.target.checked)}
                  />
                  <Label htmlFor="check-terms" className="text-sm font-normal cursor-pointer">
                    I have read and accept the Terms of Use
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="button-decline"
                  onClick={handleDecline}
                  disabled={isSubmitting}
                >
                  Decline
                </Button>
                <Button
                  type="submit"
                  data-testid="button-accept"
                  disabled={!touAccepted || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Accept'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showAdminModal} onOpenChange={setShowAdminModal}>
          <DialogContent className="sm:max-w-[400px]" data-testid="admin-modal">
            <DialogHeader>
              <DialogTitle>Employee Sign In</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Sign in with your Picture Health Google account to access admin features.
              </p>
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  hosted_domain="picturehealth.com"
                  theme={theme === 'dark' ? 'filled_black' : 'outline'}
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                />
              </div>
              {googleLoginError && (
                <p className="text-sm text-destructive text-center">{googleLoginError}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleAdminModalClose}
                data-testid="button-admin-cancel"
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
